/**
 * Comprehensive error handling and logging utilities
 */

export interface ErrorContext {
  userId?: string;
  action?: string;
  component?: string;
  timestamp: string;
  userAgent?: string;
  url?: string;
  additionalData?: Record<string, any>;
}

export interface ErrorReport {
  error: Error;
  context: ErrorContext;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'network' | 'auth' | 'payment' | 'ui' | 'api' | 'database' | 'unknown';
}

// Error categories for better classification
export const ErrorCategories = {
  NETWORK: 'network',
  AUTH: 'auth',
  PAYMENT: 'payment',
  UI: 'ui',
  API: 'api',
  DATABASE: 'database',
  UNKNOWN: 'unknown'
} as const;

// Error severity levels
export const ErrorSeverity = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
} as const;

// Global error handler class
export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorQueue: ErrorReport[] = [];
  private maxQueueSize = 100;

  private constructor() {
    this.setupGlobalErrorHandlers();
  }

  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  private setupGlobalErrorHandlers() {
    if (typeof window === 'undefined') return;

    // Handle unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      this.handleError(
        new Error(event.message),
        {
          component: 'global',
          action: 'unhandled_error',
          timestamp: new Date().toISOString(),
          url: window.location.href,
          additionalData: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
          }
        },
        ErrorSeverity.HIGH,
        ErrorCategories.UNKNOWN
      );
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(
        new Error(event.reason?.message || 'Unhandled promise rejection'),
        {
          component: 'global',
          action: 'unhandled_rejection',
          timestamp: new Date().toISOString(),
          url: window.location.href,
          additionalData: {
            reason: event.reason
          }
        },
        ErrorSeverity.HIGH,
        ErrorCategories.UNKNOWN
      );
    });
  }

  public handleError(
    error: Error,
    context: Partial<ErrorContext>,
    severity: keyof typeof ErrorSeverity = ErrorSeverity.MEDIUM,
    category: keyof typeof ErrorCategories = ErrorCategories.UNKNOWN
  ) {
    const fullContext: ErrorContext = {
      timestamp: new Date().toISOString(),
      userAgent: navigator?.userAgent,
      url: window?.location?.href,
      ...context
    };

    const errorReport: ErrorReport = {
      error,
      context: fullContext,
      severity,
      category
    };

    // Add to queue
    this.addToQueue(errorReport);

    // Log to console in development
    if (!import.meta.env.PROD) {
      console.group(`🚨 Error [${severity.toUpperCase()}] - ${category}`);
      console.error('Error:', error);
      console.log('Context:', fullContext);
      console.groupEnd();
    }

    // Send to monitoring service in production
    if (import.meta.env.PROD) {
      this.sendToMonitoring(errorReport);
    }

    // Show user-friendly message for critical errors
    if (severity === ErrorSeverity.CRITICAL) {
      this.showCriticalErrorMessage(error, category);
    }
  }

  private addToQueue(errorReport: ErrorReport) {
    this.errorQueue.push(errorReport);
    
    // Maintain queue size
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }
  }

  private async sendToMonitoring(errorReport: ErrorReport) {
    try {
      // In a real application, send to your monitoring service
      // Examples: Sentry, LogRocket, Datadog, etc.
      console.log('Sending error to monitoring service:', errorReport);
      
      // Example implementation for a monitoring service
      /*
      await fetch('/api/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorReport)
      });
      */
    } catch (monitoringError) {
      console.error('Failed to send error to monitoring service:', monitoringError);
    }
  }

  private showCriticalErrorMessage(error: Error, category: string) {
    // Show user-friendly error message for critical errors
    const message = this.getUserFriendlyMessage(error, category);
    
    // You could integrate with your toast system here
    console.error('Critical Error:', message);
  }

  private getUserFriendlyMessage(error: Error, category: string): string {
    switch (category) {
      case ErrorCategories.NETWORK:
        return 'Connection issue detected. Please check your internet connection and try again.';
      case ErrorCategories.AUTH:
        return 'Authentication error. Please log in again.';
      case ErrorCategories.PAYMENT:
        return 'Payment processing error. Please try again or contact support.';
      case ErrorCategories.DATABASE:
        return 'Data synchronization issue. Your changes may not be saved.';
      default:
        return 'An unexpected error occurred. Please refresh the page and try again.';
    }
  }

  public getErrorQueue(): ErrorReport[] {
    return [...this.errorQueue];
  }

  public clearErrorQueue() {
    this.errorQueue = [];
  }

  public getErrorStats() {
    const stats = {
      total: this.errorQueue.length,
      bySeverity: {} as Record<string, number>,
      byCategory: {} as Record<string, number>,
      recent: this.errorQueue.slice(-10)
    };

    this.errorQueue.forEach(report => {
      stats.bySeverity[report.severity] = (stats.bySeverity[report.severity] || 0) + 1;
      stats.byCategory[report.category] = (stats.byCategory[report.category] || 0) + 1;
    });

    return stats;
  }
}

// Retry mechanism for API calls
export class RetryHandler {
  private maxRetries: number;
  private baseDelay: number;
  private maxDelay: number;

  constructor(maxRetries = 3, baseDelay = 1000, maxDelay = 10000) {
    this.maxRetries = maxRetries;
    this.baseDelay = baseDelay;
    this.maxDelay = maxDelay;
  }

  async execute<T>(
    operation: () => Promise<T>,
    context: Partial<ErrorContext> = {}
  ): Promise<T> {
    let lastError: Error;
    
    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;
        
        // Don't retry on certain error types
        if (this.shouldNotRetry(error as Error)) {
          throw error;
        }

        if (attempt === this.maxRetries) {
          // Log final failure
          ErrorHandler.getInstance().handleError(
            lastError,
            {
              ...context,
              action: 'retry_exhausted',
              additionalData: { attempts: attempt }
            },
            ErrorSeverity.HIGH,
            this.categorizeError(lastError)
          );
          throw lastError;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          this.baseDelay * Math.pow(2, attempt - 1),
          this.maxDelay
        );

        console.warn(`Attempt ${attempt} failed, retrying in ${delay}ms:`, error);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  }

  private shouldNotRetry(error: Error): boolean {
    // Don't retry on authentication errors, validation errors, etc.
    const noRetryPatterns = [
      /unauthorized/i,
      /forbidden/i,
      /not found/i,
      /validation/i,
      /invalid/i,
      /bad request/i
    ];

    return noRetryPatterns.some(pattern => 
      pattern.test(error.message) || pattern.test(error.name)
    );
  }

  private categorizeError(error: Error): keyof typeof ErrorCategories {
    const message = error.message.toLowerCase();
    
    if (message.includes('network') || message.includes('fetch')) {
      return ErrorCategories.NETWORK;
    }
    if (message.includes('auth') || message.includes('unauthorized')) {
      return ErrorCategories.AUTH;
    }
    if (message.includes('payment') || message.includes('stripe')) {
      return ErrorCategories.PAYMENT;
    }
    if (message.includes('database') || message.includes('supabase')) {
      return ErrorCategories.DATABASE;
    }
    
    return ErrorCategories.UNKNOWN;
  }
}

// Enhanced async wrapper with error handling
export const withErrorHandling = <T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  context: Partial<ErrorContext> = {},
  retryOptions?: { maxRetries?: number; baseDelay?: number }
) => {
  return async (...args: T): Promise<R> => {
    const retryHandler = new RetryHandler(
      retryOptions?.maxRetries,
      retryOptions?.baseDelay
    );

    return retryHandler.execute(
      () => fn(...args),
      context
    );
  };
};

// Input validation with error handling
export const validateWithErrorHandling = (
  value: any,
  validator: (value: any) => boolean,
  errorMessage: string,
  context: Partial<ErrorContext> = {}
): boolean => {
  try {
    const isValid = validator(value);
    if (!isValid) {
      ErrorHandler.getInstance().handleError(
        new Error(errorMessage),
        {
          ...context,
          action: 'validation_failed',
          additionalData: { value: typeof value === 'string' ? value.substring(0, 100) : value }
        },
        ErrorSeverity.LOW,
        ErrorCategories.UI
      );
    }
    return isValid;
  } catch (error) {
    ErrorHandler.getInstance().handleError(
      error as Error,
      {
        ...context,
        action: 'validation_error'
      },
      ErrorSeverity.MEDIUM,
      ErrorCategories.UI
    );
    return false;
  }
};

// Initialize error handling
export const initializeErrorHandling = () => {
  return ErrorHandler.getInstance();
};